'use client';

import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardAction,
} from '@packages/shared/src/ui/Card/Card';
import { useIntl, defineMessages } from 'react-intl';
import type { MessageTag, NoMessageValues } from 'react-intl';
import type { CustomerStatus } from '@packages/gql/generated/shopping/graphql';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { Link } from '@packages/modules/src/Link/Link';
import { AccountItemCard } from './AccountItemCard';

const messages = defineMessages<{
  readonly cardHeadline: NoMessageValues;
  readonly emptyMessage: NoMessageValues;
  readonly hintText: { readonly link: MessageTag };
  readonly allTransactions: NoMessageValues;
}>({
  cardHeadline: {
    id: 'ReturnsCard.headline',
    defaultMessage: 'Meine Rücksendungen',
  },
  emptyMessage: {
    id: 'ReturnsCard.empty.message',
    defaultMessage: 'Bei uns ist keine Rücksendung eingegangen.',
  },
  hintText: {
    id: 'ReturnsCard.hintText',
    defaultMessage:
      'Die Gutschrift zu deinen Rücksendung findest du unter <link>Meine Buchungen - Meine Transaktionen</link>',
  },
  allTransactions: {
    id: 'ReturnsCard.allTransactions',
    defaultMessage: 'Alle Transaktionen',
  },
});

export type ReturnsCardProps = {
  /** List of recent returns to display */
  returns?: CustomerStatus['returns'];
};

/**
 * ReturnsCard component displays recent returns information
 * on the my account dashboard
 */
export const ReturnsCard = ({ returns = [] }: ReturnsCardProps) => {
  const {
    order: {
      paths: { myAccountBookings },
    },
  } = useConfig();
  const { formatMessage, locale } = useIntl();
  const hasReturns = returns && returns.length > 0;

  // Construct URLs for my transactions - directly use myAccountBookings path
  const myTransactionsUrl = `${getLocalizedPath(myAccountBookings, locale)}/#transaktionen?filter=returns`;
  const allTransactionsUrl = `${getLocalizedPath(myAccountBookings, locale)}/#transaktionen`;
  const visibleReturns = hasReturns ? returns.slice(0, 3) : [];
  return (
    <Card hasAutoSeparation>
      <CardHeader hasDivider>
        <h2 className="text-size-headline-base">{formatMessage(messages.cardHeadline)}</h2>
      </CardHeader>

      <CardContent className="flex flex-col gap-1.5 py-1.5">
        {!hasReturns ? (
          <p className="text-color-text-dark text-size-body-xs">
            {formatMessage(messages.emptyMessage)}
          </p>
        ) : (
          <>
            {/* Hint text section */}
            <div className="text-color-text-dark text-size-body-xs">
              {formatMessage(messages.hintText, {
                link: (chunk: React.ReactNode) => (
                  <Link href={myTransactionsUrl} className="underline hover:no-underline">
                    {chunk}
                  </Link>
                ),
              })}
            </div>
            {/* Returns items */}
            {visibleReturns.map((returnItem) => {
              // Show max 3 items, rest go to additional count
              const visibleItems = returnItem.items.slice(0, 3);
              const additionalCount =
                (returnItem.additionalItemCount || 0) + Math.max(0, returnItem.items.length - 4);
              return (
                <AccountItemCard
                  key={`${returnItem.orderNo}-${returnItem.date}`}
                  items={visibleItems}
                  additionalCount={additionalCount}
                  orderNo={returnItem.orderNo}
                />
              );
            })}
          </>
        )}
      </CardContent>
      {/* Action link section */}
      <CardFooter className="flex items-center gap-2 px-2 py-1">
        <CardAction icon="icon-emp-billing">
          <Link
            href={allTransactionsUrl}
            className="text-color-text-dark text-size-body-sm flex items-center hover:underline"
          >
            {formatMessage(messages.allTransactions)}
          </Link>
        </CardAction>
      </CardFooter>
    </Card>
  );
};
